import Navbar from "../components/Navbar";
import Footer from "../components/Footer";
import { useState, useRef } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { ChevronLeft, ChevronRight, Maximize2, Check, Wifi, Zap, Home } from "lucide-react";
import { useSaunaBasePrice } from "../hooks/useSaunaBasePrice";

const galleryImages = {
  exterior: [
    "https://myodoo-pl-lykke.odoo.com/web/image/product.image/498/image_1024/bnb17%20%2810%29.webp?unique=f5701cd",
    "https://myodoo-pl-lykke.odoo.com/web/image/product.image/499/image_1024/bnb17%20%283%29.webp?unique=f5701cd",
    "https://myodoo-pl-lykke.odoo.com/web/image/product.image/500/image_1024/bnb17%20%284%29.webp?unique=f5701cd",
    "https://myodoo-pl-lykke.odoo.com/web/image/product.image/501/image_1024/bnb17%20%288%29.webp?unique=f5701cd",
  ],
  interior: [
    "https://myodoo-pl-lykke.odoo.com/web/image/product.image/502/image_1024/bnb17.webp?unique=f5701cd",
    "https://myodoo-pl-lykke.odoo.com/web/image/product.image/503/image_1024/bnb17%20%286%29.webp?unique=f5701cd",
    "https://myodoo-pl-lykke.odoo.com/web/image/product.image/504/image_1024/bnb17%20%289%29.webp?unique=f5701cd",
    "https://myodoo-pl-lykke.odoo.com/web/image/product.image/505/image_1024/bnb17%20%287%29.webp?unique=f5701cd",
  ],
  details: [
    "https://myodoo-pl-lykke.odoo.com/web/image/product.image/506/image_1024/bnb17%20%285%29.webp?unique=f5701cd",
    "https://myodoo-pl-lykke.odoo.com/web/image/product.image/507/image_1024/bnb17%20%282%29.webp?unique=f5701cd",
    "https://myodoo-pl-lykke.odoo.com/web/image/16428-3fce7332/bnb17%20%289%29.webp?access_token=95f2d7ab-8272-4d38-912c-96672b88e58a",
    "https://myodoo-pl-lykke.odoo.com/web/image/16429-4184a43f/bnb17%20%2812%29.webp?access_token=b65fa26f-0f57-4468-a9e9-86bb19c64ad0",
  ],
  all: [],
};

galleryImages.all = [...galleryImages.exterior, ...galleryImages.interior, ...galleryImages.details];

const otherProducts = [
  { label: "NANO NOMAD", href: "/sauna-nomad", img: "https://myodoo-pl-lykke.odoo.com/web/image/16838-946abc4b/nano%20mobile.jpeg?access_token=af9ee5ae-d560-4062-942f-d860a7634e00" },
  { label: "UMI", href: "/sauna-umi", img: "https://media.base44.com/images/public/69a726345560d5ef49a37111/2bea1da1e_LYKKEUMI.jpg" },
  { label: "MORI", href: "/sauna-mori", img: "https://media.base44.com/images/public/69a726345560d5ef49a37111/4557c280d_LYKKE_MORI_1.jpg" },
  { label: "HIRO", href: "/sauna-hiro", img: "https://media.base44.com/images/public/69a726345560d5ef49a37111/3d5fe43e4_HIRO_1.jpg" },
];

function LightboxGallery({ images, initialIndex, onClose }) {
  const [idx, setIdx] = useState(initialIndex);
  return (
    <div className="fixed inset-0 bg-black z-50 flex flex-col" onClick={onClose}>
      <button onClick={onClose} className="absolute top-6 right-8 text-white/70 hover:text-white text-3xl z-10">×</button>
      <div className="flex-1 flex items-center justify-center relative" onClick={e => e.stopPropagation()}>
        <AnimatePresence mode="wait">
          <motion.img
            key={idx}
            src={images[idx]}
            alt=""
            className="max-h-[85vh] max-w-[90vw] object-contain"
            initial={{ opacity: 0, scale: 0.97 }}
            animate={{ opacity: 1, scale: 1 }}
            exit={{ opacity: 0, scale: 0.97 }}
            transition={{ duration: 0.25 }}
          />
        </AnimatePresence>
        <button onClick={() => setIdx(i => (i - 1 + images.length) % images.length)} className="absolute left-4 top-1/2 -translate-y-1/2 text-white/60 hover:text-white p-2"><ChevronLeft size={32} /></button>
        <button onClick={() => setIdx(i => (i + 1) % images.length)} className="absolute right-4 top-1/2 -translate-y-1/2 text-white/60 hover:text-white p-2"><ChevronRight size={32} /></button>
      </div>
      <div className="flex gap-2 justify-center py-4 overflow-x-auto px-4">
        {images.map((img, i) => (
          <button key={i} onClick={e => { e.stopPropagation(); setIdx(i); }} className={`flex-shrink-0 w-14 h-10 overflow-hidden transition-all ${i === idx ? 'ring-2 ring-white' : 'opacity-40 hover:opacity-70'}`}>
            <img src={img} alt="" className="w-full h-full object-cover" />
          </button>
        ))}
      </div>
    </div>
  );
}

export default function TinyBnb17() {
  const { basePrice: price } = useSaunaBasePrice('TINY BNB 17M', 46500);
  const [lightbox, setLightbox] = useState(null);
  const [activeGallery, setActiveGallery] = useState('exterior');
  const stickyRef = useRef(null);

  return (
    <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ duration: 0.5 }} className="bg-[#f5f4f0]">
      <Navbar />

      {/* ── HERO ── */}
      <div className="relative w-full h-[75vh] md:h-[90vh] overflow-hidden">
        <img
          src={galleryImages.exterior[0]}
          alt="TINY BNB 17M"
          className="absolute inset-0 w-full h-full object-cover"
        />
        <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-black/20 to-transparent pointer-events-none" />

        <div className="absolute bottom-0 left-0 px-8 md:px-16 pb-14 md:pb-20">
          <p className="text-white/60 text-xs tracking-[0.3em] uppercase font-body mb-3">KOLEKSJON</p>
          <h1 className="font-heading text-5xl md:text-7xl lg:text-8xl font-light text-white leading-[1] mb-3">
            TINY BNB 17M
          </h1>
          <p className="font-heading text-3xl md:text-5xl italic text-white/70 font-light leading-tight mb-4">
            Nøkkelferdig B&B og investering
          </p>
          <p className="text-white/60 text-lg md:text-xl tracking-[0.2em] uppercase font-body">
            FULLSTEDIIG UTSTYRT · MOBIL · 660 × 250 × 330 CM
          </p>
        </div>
      </div>

      {/* ── OVERVIEW ── */}
      <div className="max-w-[1440px] mx-auto px-6 md:px-12 py-16 md:py-24">
        <div className="grid md:grid-cols-2 gap-16 items-start">
          <div ref={stickyRef} className="md:sticky md:top-32">
            <p className="text-xs tracking-[0.25em] uppercase text-foreground/50 mb-4 font-body">LYKKE BNB · Tiny House</p>
            <h1 className="font-heading text-5xl md:text-6xl lg:text-7xl font-light leading-[1.05] mb-6">
              BNB 17<br />
              <span className="italic opacity-40 text-4xl md:text-5xl">Kort leie</span>
            </h1>
            <p className="text-foreground/60 leading-relaxed mb-10 text-base max-w-sm">
              Full uavhengighet og komfort med Airbnb-potensialet. Nøkkelferdig hus på 17 m² med moderne interiør, kjøkken, bad og klimaanlegg. Ideell som investeringseiendom eller permanent bolig.
            </p>

            {/* Spec table */}
            <div className="space-y-0 border-t border-foreground/10 mb-10">
              {[
                ["Størrelse", "~17 m² (14 m² + 3 m² hems)"],
                ["Startpris", `Fra €${price.toLocaleString('nb-NO')}`],
                ["Leveringstid", "12–16 uker"],
                ["Personer", "Komfortabelt 2–4"],
                ["Layout", "Stue + kjøkken, bad, soverom"],
                ["Energi", "Varmepumpe 9000 BTU (A++)"],
              ].map(([label, val]) => (
                <div key={label} className="grid grid-cols-2 border-b border-foreground/10 py-2.5 gap-4">
                  <span className="text-lg text-foreground/40 font-body tracking-wide">{label}</span>
                  <span className="text-xl text-foreground font-body font-medium">{val}</span>
                </div>
              ))}
            </div>

            <div className="flex flex-col gap-3">
              <button
                onClick={() => window.location.href = "/be-om-tilbud"}
                className="w-full md:w-auto px-10 py-4 bg-foreground text-background text-sm tracking-[0.15em] uppercase font-body hover:bg-foreground/85 transition-all duration-200 rounded-sm"
              >
                Be om tilbud
              </button>
              <p className="text-base text-foreground/60 font-body">
                Spørsmål?{" "}
                <a href="/be-om-tilbud" className="underline hover:text-foreground transition-colors">Be om tilbud</a>
                {" "}eller{" "}
                <a href="/om-oss" className="underline hover:text-foreground transition-colors">kontakt oss</a>.
              </p>
            </div>
          </div>

          <div className="space-y-10 pt-2">
            <div className="bg-[#f0ede6] rounded-lg p-6">
              <h3 className="font-heading text-2xl font-light mb-4 flex items-center gap-2">
                <Home className="w-6 h-6 text-[#4a8f63]" />
                Nøkkelferdig fra dag én
              </h3>
              <ul className="space-y-3 text-foreground/60">
                <li className="flex gap-3">
                  <Check className="w-5 h-5 text-[#4a8f63] flex-shrink-0" />
                  <span>Komplett kjøkken (IKEA Metod) + bad</span>
                </li>
                <li className="flex gap-3">
                  <Check className="w-5 h-5 text-[#4a8f63] flex-shrink-0" />
                  <span>Naturlig treverk + moderne design</span>
                </li>
                <li className="flex gap-3">
                  <Check className="w-5 h-5 text-[#4a8f63] flex-shrink-0" />
                  <span>LED-belysning innvendig og utvendig</span>
                </li>
                <li className="flex gap-3">
                  <Check className="w-5 h-5 text-[#4a8f63] flex-shrink-0" />
                  <span>Alle installasjoner + elektronikk</span>
                </li>
              </ul>
            </div>

            <div className="bg-gradient-to-br from-blue-50 to-cyan-50 rounded-lg p-6 border border-blue-100">
              <h3 className="font-heading text-2xl font-light mb-4 flex items-center gap-2">
                <Zap className="w-6 h-6 text-blue-600" />
                Energieffektivt og smart
              </h3>
              <p className="text-foreground/60 mb-4">Inverter varmepumpe 9000 BTU (A++), LED-belysning, elektrisk oppvarming og intelligent styring.</p>
              <p className="text-sm text-foreground/50">Ideell for ganzjährig bruk eller sesongbasert utleie.</p>
            </div>

            <div className="p-6 border border-foreground/10">
              <h3 className="font-heading text-xl font-light mb-3">Investeringspotensial</h3>
              <ul className="space-y-2 text-foreground/60 text-sm">
                <li>✓ Airbnb/Booking.com – kort leie</li>
                <li>✓ Lavt vedlikehold – enkelt å drifte</li>
                <li>✓ Mobil – lett å flytte ved behov</li>
                <li>✓ 10 år garanti på konstruksjon</li>
              </ul>
            </div>
          </div>
        </div>
      </div>

      {/* ── GALLERY TABS ── */}
      <div className="max-w-[1440px] mx-auto px-6 md:px-12 py-8 border-b border-foreground/10">
        <div className="flex gap-4 md:gap-8 overflow-x-auto pb-4">
          {[
            { id: 'exterior', label: 'Eksteriør' },
            { id: 'interior', label: 'Interiør' },
            { id: 'details', label: 'Detaljer' },
            { id: 'all', label: 'Alle bilder' },
          ].map((tab) => (
            <button
              key={tab.id}
              onClick={() => setActiveGallery(tab.id)}
              className={`font-body text-sm tracking-[0.15em] uppercase whitespace-nowrap pb-3 transition-all ${
                activeGallery === tab.id
                  ? 'text-foreground border-b-2 border-foreground'
                  : 'text-foreground/50 hover:text-foreground'
              }`}
            >
              {tab.label}
            </button>
          ))}
        </div>
      </div>

      {/* ── PHOTO GRID GALLERY ── */}
      <div className="px-1 py-12">
        <AnimatePresence mode="wait">
          <motion.div
            key={activeGallery}
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.3 }}
            className="grid grid-cols-2 md:grid-cols-4 gap-1"
          >
            {galleryImages[activeGallery].map((img, i) => (
              <div
                key={i}
                className="overflow-hidden cursor-pointer"
                style={{ height: '280px' }}
                onClick={() => setLightbox({ images: galleryImages.all, idx: i })}
              >
                <img src={img} alt={`Galeria ${i + 1}`} className="w-full h-full object-cover hover:scale-105 transition-transform duration-300" />
              </div>
            ))}
          </motion.div>
        </AnimatePresence>
      </div>

      {/* ── NØKKELPUNKTER ── */}
      <div className="bg-[#f0ede6] py-20 md:py-24 mt-20 md:mt-24">
        <div className="max-w-[1440px] mx-auto px-6 md:px-12">
          <h2 className="font-heading text-3xl md:text-5xl font-light leading-[1.2] mb-12">Hvorfor TINY BNB 17?</h2>
          <div className="grid md:grid-cols-3 gap-10">
            {[
              {
                title: "Investering med avkastning",
                text: "Perfekt for korttidsutleie på plattformer som Airbnb/Booking. Lav driftskostnad = høy marginal."
              },
              {
                title: "Fleksibel bruk",
                text: "Kan brukes som permanent bolig, sesongbasert fritidshus eller dedikert utleieeiendom."
              },
              {
                title: "Komplett og moderne",
                text: "Ferdig innredet med IKEA-kjøkken, moderne bad og alle installasjoner. Ingen byggeprosess."
              },
              {
                title: "Energieffektivt",
                text: "Varmepumpe 9000 BTU, LED-belysning, sterk isolasjon — lave strømregninger gjennom året."
              },
              {
                title: "Robust og holdbar",
                text: "Sertifisert konstruksjon, godkjente materialer, 10 års garanti på struktur."
              },
              {
                title: "Mobil løsning",
                text: "Montert på tilhenger — lett å transportere og plassere hvor som helst med strøm/vann."
              },
            ].map((item) => (
              <motion.div key={item.title} whileHover={{ y: -8 }} transition={{ duration: 0.3 }}>
                <h3 className="font-heading text-xl font-normal mb-3">{item.title}</h3>
                <p className="text-foreground/60 text-lg leading-relaxed">{item.text}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </div>

      {/* ── SPECYFIKACJA TECHNICZNA ── */}
      <div className="max-w-[1440px] mx-auto px-6 md:px-12 py-20 md:py-24">
        <h2 className="font-heading text-3xl md:text-5xl font-light leading-[1.2] mb-12">Pełna specyfikacja</h2>
        <div className="grid md:grid-cols-2 gap-12">
          <div className="space-y-8">
            <div>
              <h3 className="font-heading text-xl font-light mb-4 border-b border-foreground/20 pb-3">Wymiary i pojemność</h3>
              <dl className="space-y-3 text-foreground/60">
                <div className="flex justify-between"><dt className="font-body">Wymiary zewnętrzne</dt><dd className="text-foreground font-medium">660 × 250 × 330 cm</dd></div>
                <div className="flex justify-between"><dt className="font-body">Wymiary wewnętrzne</dt><dd className="text-foreground font-medium">620 × 205 × 280 cm</dd></div>
                <div className="flex justify-between"><dt className="font-body">Bruksareal</dt><dd className="text-foreground font-medium">~17 m² (14 m² + 3 m² hems)</dd></div>
                <div className="flex justify-between"><dt className="font-body">Bad</dt><dd className="text-foreground font-medium">Ca. 2,5 m²</dd></div>
                <div className="flex justify-between"><dt className="font-body">Kapasitet</dt><dd className="text-foreground font-medium">2 personer komfortabelt, maks 4</dd></div>
              </dl>
            </div>

            <div>
              <h3 className="font-heading text-xl font-light mb-4 border-b border-foreground/20 pb-3">Konstruksjon</h3>
              <dl className="space-y-3 text-foreground/60">
                <div className="flex justify-between"><dt className="font-body">Rammeverk</dt><dd className="text-foreground font-medium">Gran C24 (skandynawski)</dd></div>
                <div className="flex justify-between"><dt className="font-body">Veggiso</dt><dd className="text-foreground font-medium">Mineralull 100 mm</dd></div>
                <div className="flex justify-between"><dt className="font-body">Takiso</dt><dd className="text-foreground font-medium">STEICO 150 mm</dd></div>
                <div className="flex justify-between"><dt className="font-body">Paroizolacija</dt><dd className="text-foreground font-medium">Hunton Intello Plus</dd></div>
                <div className="flex justify-between"><dt className="font-body">Vindvern</dt><dd className="text-foreground font-medium">BILKA 150 g/m²</dd></div>
              </dl>
            </div>
          </div>

          <div className="space-y-8">
            <div>
              <h3 className="font-heading text-xl font-light mb-4 border-b border-foreground/20 pb-3">Interiør og utstyr</h3>
              <dl className="space-y-3 text-foreground/60">
                <div className="flex justify-between"><dt className="font-body">Kjøkken</dt><dd className="text-foreground font-medium">IKEA Metod + Fröjered</dd></div>
                <div className="flex justify-between"><dt className="font-body">Gulv</dt><dd className="text-foreground font-medium">Laminat 8 mm / SPC bad</dd></div>
                <div className="flex justify-between"><dt className="font-body">Veggdekor</dt><dd className="text-foreground font-medium">Gran, Softline profil 120 mm</dd></div>
                <div className="flex justify-between"><dt className="font-body">Vinduer</dt><dd className="text-foreground font-medium">PVC Salamander antracyt</dd></div>
                <div className="flex justify-between"><dt className="font-body">Belysning</dt><dd className="text-foreground font-medium">LED innvendig og utvendig</dd></div>
              </dl>
            </div>

            <div>
              <h3 className="font-heading text-xl font-light mb-4 border-b border-foreground/20 pb-3">Installasjoner</h3>
              <dl className="space-y-3 text-foreground/60">
                <div className="flex justify-between"><dt className="font-body">Elektrisitet</dt><dd className="text-foreground font-medium">230V komplett</dd></div>
                <div className="flex justify-between"><dt className="font-body">Klima/Varme</dt><dd className="text-foreground font-medium">Inverter varmepumpe 9000 BTU (A++)</dd></div>
                <div className="flex justify-between"><dt className="font-body">Varmtvann</dt><dd className="text-foreground font-medium">Varmtvannsbereder 40L</dd></div>
                <div className="flex justify-between"><dt className="font-body">Venting</dt><dd className="text-foreground font-medium">Desentralisert med varmegjenvinning</dd></div>
                <div className="flex justify-between"><dt className="font-body">Fasade</dt><dd className="text-foreground font-medium">Skandinavisk furu / stål</dd></div>
              </dl>
            </div>
          </div>
        </div>
      </div>

      {/* ── GARANTIER ── */}
      <div className="bg-[#f0ede6] py-20 md:py-24">
        <div className="max-w-[1440px] mx-auto px-6 md:px-12">
          <div className="grid md:grid-cols-3 gap-10">
            <div className="bg-white rounded-lg p-8">
              <h3 className="font-heading text-2xl font-light mb-4">10 år struktur</h3>
              <p className="text-foreground/60 leading-relaxed">Plønn garanti på hele konstruksjonen og bæresystem fra kjøp.</p>
            </div>
            <div className="bg-white rounded-lg p-8">
              <h3 className="font-heading text-2xl font-light mb-4">2 år utstyr</h3>
              <p className="text-foreground/60 leading-relaxed">Garanti på alle hvitevarer, klimaanlegg og elektronikk fra produsent.</p>
            </div>
            <div className="bg-white rounded-lg p-8">
              <h3 className="font-heading text-2xl font-light mb-4">Vedlikehold</h3>
              <p className="text-foreground/60 leading-relaxed">Fasade beskyttes med naturlig olje/maling. Sjekk hver 8–10 år.</p>
            </div>
          </div>
        </div>
      </div>

      {/* ── CTA ── */}
      <div className="max-w-[1440px] mx-auto px-6 md:px-12 py-24">
        <div className="text-center">
          <h2 className="font-heading text-3xl md:text-5xl font-light leading-[1.2] mb-6">
            Klar for å starte investeringen?
          </h2>
          <p className="text-foreground/60 text-xl leading-relaxed mb-12 max-w-2xl mx-auto">
            Be om detaljert tilbud, diskuter tilpassinger eller få mer informasjon om TINY BNB 17M og mulighetene.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <button
              onClick={() => window.location.href = "/be-om-tilbud"}
              className="px-10 py-4 bg-foreground text-background text-sm tracking-[0.15em] uppercase font-body hover:bg-foreground/85 transition-all duration-200 rounded-sm"
            >
              Be om tilbud
            </button>
            <button
              onClick={() => window.location.href = "/om-oss"}
              className="px-10 py-4 border border-foreground/30 text-foreground text-sm tracking-[0.15em] uppercase font-body hover:bg-foreground hover:text-background transition-all duration-200 rounded-sm"
            >
              Kontakt oss
            </button>
          </div>
        </div>
      </div>

      {/* ── BROWSE RANGE ── */}
      <div className="max-w-[1440px] mx-auto px-6 md:px-12 pb-24">
        <h2 className="font-heading text-3xl font-light mb-10">Andre produkter</h2>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          {otherProducts.map((product) => (
            <a
              key={product.label}
              href={product.href}
              className="group block overflow-hidden"
            >
              <div className="aspect-[3/4] overflow-hidden mb-3 rounded-sm">
                <img
                  src={product.img}
                  alt={product.label}
                  className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105"
                />
              </div>
              <div className="flex items-center justify-between">
                <span className="font-heading text-xl font-light">{product.label}</span>
                <span className="text-sm text-foreground/40 group-hover:text-foreground transition-colors tracking-widest uppercase font-body">Se →</span>
              </div>
            </a>
          ))}
        </div>
      </div>

      <Footer />

      {lightbox && (
        <LightboxGallery
          images={lightbox.images}
          initialIndex={lightbox.idx}
          onClose={() => setLightbox(null)}
        />
      )}
    </motion.div>
  );
}